import React, { createContext, useContext, useMemo } from 'react';

/**
 * Host-supplied route. The provider derives `segment`; callers do not
 * pass it. CE reads this object and never `window`.
 */
export interface CeRouteInput {
  readonly path: string;
  readonly segments: readonly string[];
  readonly hash: string;
  readonly query: Readonly<Record<string, string>>;
}

/** Route bag published to interpolation as `route`. */
export interface CeRoute extends CeRouteInput {
  readonly segment: string;
}

export function deriveRouteSegment(segments: readonly string[]): string {
  if (segments.length === 0) {
    return '';
  }
  const last = segments[segments.length - 1];
  return last ?? '';
}

const RouteContext = createContext<CeRoute | undefined>(undefined);

export interface RouteProviderProps {
  readonly route: CeRouteInput;
  readonly children: React.ReactNode;
}

/**
 * Publishes the host route for `{{route.*}}`. Same seam as the action
 * dispatcher: a wrapper, not a prop on the flow engine. Tests and hosts
 * inject the value; nothing here reads the browser location.
 */
export const RouteProvider: React.FC<RouteProviderProps> = ({
  route,
  children,
}) => {
  const value = useMemo<CeRoute>(
    () => ({
      path: route.path,
      segments: route.segments,
      hash: route.hash,
      query: route.query,
      segment: deriveRouteSegment(route.segments),
    }),
    [route],
  );

  return (
    <RouteContext.Provider value={value}>{children}</RouteContext.Provider>
  );
};

/** Nearest injected route, or `undefined` when no provider is mounted. */
export function useRoute(): CeRoute | undefined {
  return useContext(RouteContext);
}
